import React from 'react'

import useScreenSize from './screenSize'

const SIDEBAR_RAIL_EXCLUSIVE_BREAK = 1500

type LayoutPanelsContextValue = {
  sidebarIsOpen: boolean
  rightRailIsOpen: boolean
  toggleSidebar: () => void
  closeSidebar: () => void
  setRightRailIsOpen: (open: boolean) => void
  registerRightRailClose: (close: () => void) => () => void
  onRightRailOpened: () => void
}

const LayoutPanelsContext = React.createContext<LayoutPanelsContextValue | null>(null)

export function useLayoutPanelsOptional(): LayoutPanelsContextValue | null {
  return React.useContext(LayoutPanelsContext)
}

export const LayoutPanelsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarIsOpen, setSidebarIsOpen] = React.useState(false)
  const [rightRailIsOpen, setRightRailIsOpen] = React.useState(false)
  const rightRailCloseRef = React.useRef<(() => void) | null>(null)
  const { screenSize } = useScreenSize()
  const isExclusive = screenSize.width <= SIDEBAR_RAIL_EXCLUSIVE_BREAK

  const closeSidebar = React.useCallback(() => setSidebarIsOpen(false), [])

  const toggleSidebar = React.useCallback(() => {
    setSidebarIsOpen((prev) => {
      const next = !prev
      if (next && isExclusive) {
        rightRailCloseRef.current?.()
      }
      return next
    })
  }, [isExclusive])

  const registerRightRailClose = React.useCallback((close: () => void) => {
    rightRailCloseRef.current = close
    return () => {
      if (rightRailCloseRef.current === close) {
        rightRailCloseRef.current = null
      }
    }
  }, [])

  const onRightRailOpened = React.useCallback(() => {
    if (isExclusive) {
      setSidebarIsOpen(false)
    }
  }, [isExclusive])

  const setRightRailIsOpenStable = React.useCallback((open: boolean) => {
    setRightRailIsOpen(open)
  }, [])

  const value = React.useMemo(
    () => ({
      sidebarIsOpen,
      rightRailIsOpen,
      toggleSidebar,
      closeSidebar,
      setRightRailIsOpen: setRightRailIsOpenStable,
      registerRightRailClose,
      onRightRailOpened,
    }),
    [
      sidebarIsOpen,
      rightRailIsOpen,
      toggleSidebar,
      closeSidebar,
      setRightRailIsOpenStable,
      registerRightRailClose,
      onRightRailOpened,
    ]
  )

  return (
    <LayoutPanelsContext.Provider value={value}>
      {children}
    </LayoutPanelsContext.Provider>
  )
}

export const SIDEBAR_RAIL_EXCLUSIVE_MAX_WIDTH = SIDEBAR_RAIL_EXCLUSIVE_BREAK
